import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  HttpClient,
  isObject,
  Logger,
  LoggerTransformer,
  Platform,
  ServiceBaseUrl,
} from "@identity/utils";
import { WINDOW } from "@identity/symbols";
import { InstantConfigService } from "@identity/services";
import isDebug from "../../utils/is-debug";

type Meter = "histogram" | "counter";
type MeterPayload<T extends Meter = Meter> = {
  histogram: { value: string };
  counter: Record<string, never>;
}[T];

type Action =
  | "document_content_loaded"
  | "document_load_time"
  | "init"
  | "ready"
  | "optin"
  | "optout"
  | "live-connect-requested";

@singleton()
export class MetricReporterService {
  private enabled = false;
  private readonly monitoringBaseUrl: string;

  constructor(
    @inject(ServiceBaseUrl) serviceBaseUrl: ServiceBaseUrl,
    @inject(Platform) private readonly platform: Platform,
    @inject(WINDOW) private readonly window: Window,
    @inject(HttpClient) private readonly httpClient: HttpClient,
    @injectWithTransform(Logger, LoggerTransformer, "MetricReporterService")
    private readonly logger: Logger,
  ) {
    this.monitoringBaseUrl = `${serviceBaseUrl.get()}adeng/api/identity/meters/`;
  }

  private isReportingEnabled(
    instantConfigService: InstantConfigService,
  ): boolean {
    const threshold = instantConfigService.get("icMonitorTrafficThreshold", {
      identity: 0,
    });

    return isDebug()
      ? true
      : isObject(threshold) && threshold?.identity > 0
        ? threshold.identity >= Math.random() * 100
        : false;
  }

  public init(instantConfigService: InstantConfigService): void {
    this.enabled = this.isReportingEnabled(instantConfigService);

    this.logger.info(`Metric reporter enabled: ${this.enabled}`);

    if (this.enabled) {
      if (this.window.document.readyState === "complete") {
        this.reportPerformanceMetrics();
      } else {
        this.window?.addEventListener("load", () =>
          this.reportPerformanceMetrics(),
        );
      }
    }
  }

  public reportIdentityEngineState(state: "ready" | "init") {
    if (!this.enabled) return;

    this.reportTimedAction(state);
  }

  public reportConsentEvent(value: boolean) {
    if (!this.enabled) return;

    this.reportAction(`opt${value ? "in" : "out"}`);
  }

  private reportPerformanceMetrics() {
    const perfNav = this.window.performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];
    if (perfNav.length > 0) {
      const documentContentLoaded = perfNav[0].domContentLoadedEventEnd;
      const documentLoadTime = perfNav[0].loadEventEnd;
      this.reportTimedAction(
        "document_content_loaded",
        Math.round(
          documentContentLoaded > 0
            ? documentContentLoaded
            : this.logger.getTimeDelta(),
        ),
      );
      this.reportTimedAction(
        "document_load_time",
        Math.round(
          documentLoadTime > 0 ? documentLoadTime : this.logger.getTimeDelta(),
        ),
      );
    }
  }

  private send<T extends Meter>(
    meter: T,
    action: Action,
    payload: MeterPayload<T>,
  ) {
    if (!this.enabled) return;

    const url = new URL(meter, this.monitoringBaseUrl);
    url.searchParams.set("app", this.platform.get());
    url.searchParams.set("action", action);
    Object.entries(payload).forEach(([key, value]) =>
      url.searchParams.set(key, value),
    );

    this.httpClient
      .get(
        url.toString(),
        {
          mode: "cors",
        },
        2000,
      )
      .catch(() => {
        this.logger.error("Failed to send data");
      });
  }

  public reportAction(action: Action) {
    if (!this.enabled) return;

    this.send("counter", action, {});
  }

  public reportTimedAction(action: Action, duration?: number) {
    if (!this.enabled) return;

    const payload = {
      value: duration
        ? duration.toString()
        : Math.round(this.logger.getTimeDelta()).toString(),
    };

    this.send("histogram", action, payload);
  }
}
